import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Users, Upload, FileText, Trash2, Download, User, Phone, Mail, MapPin, Calendar, DollarSign, Building, CreditCard, Search, ChevronRight, Eye, X, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase() || '??';
};

const documentTypes = [
  { value: 'id', label: 'Identificación' },
  { value: 'contract', label: 'Contrato' },
  { value: 'resume', label: 'Currículum' },
  { value: 'certification', label: 'Certificación' },
  { value: 'license', label: 'Licencia' },
  { value: 'medical', label: 'Médico' },
  { value: 'tax', label: 'Fiscal' },
  { value: 'other', label: 'Otro' }
];

const emptyProfile = {
  phone: '', address: '', city: '', zipcode: '', country: '', date_of_birth: '', gender: '', marital_status: '',
  nationality: '', id_number: '', department: '', position: '', hire_date: '',
  employment_type: '', worker_classification: '', salary: '', hourly_rate: '', pay_frequency: '', bank_name: '', bank_account: '',
  routing_number: '', account_type: 'checking',
  tax_retention_percent: 0,
  accrual_law: 'post_2017',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '', notes: ''
};

const accrualLawOptions = [
  { value: 'pre_2017_115', label: 'Ley Vieja (pre-2017, ≥115h)', desc: '1.25 vac + 1.00 enf/mes' },
  { value: 'pre_2017_130', label: 'Contratado antes 26/ene/2017 (≥130h)', desc: '1.25 vac + 1.00 enf/mes' },
  { value: 'post_2017', label: 'Contratado desde 26/ene/2017 (≥130h)', desc: 'Vacaciones progresivas + 1.00 enf/mes' },
  { value: 'small_employer', label: 'Patrono ≤12 empleados (≥130h)', desc: '0.50 vac + 1.00 enf/mes' },
];

const Field = ({ label, icon: Icon, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </Label>
    {children}
  </div>
);

const HumanResourcesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ document_type: '', file: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('info');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [employeeStubs, setEmployeeStubs] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loadingStubs, setLoadingStubs] = useState(false);

  const updateProfile = useCallback((field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.get('/hr/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async (userId) => {
    try {
      const response = await axios.get(`/hr/employees/${userId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      setDocuments([]);
    }
  };

  const loadEmployeeStubs = async (userId) => {
    setLoadingStubs(true);
    try {
      const response = await axios.get(`/pay-stubs/employee/${userId}`);
      setEmployeeStubs(response.data || []);
    } catch (error) {
      setEmployeeStubs([]);
    } finally {
      setLoadingStubs(false);
    }
  };

  useEffect(() => { loadEmployees(); }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadDocuments(selectedEmployee.user_id);
      loadEmployeeStubs(selectedEmployee.user_id);
      loadLeaveBalance(selectedEmployee.user_id);
      const emp = selectedEmployee.profile || {};
      setProfile({
        phone: emp.phone || '', address: emp.address || '', city: emp.city || '', zipcode: emp.zipcode || '',
        country: emp.country || '', date_of_birth: emp.date_of_birth || '', gender: emp.gender || '',
        marital_status: emp.marital_status || '', nationality: emp.nationality || '', id_number: emp.id_number || '',
        department: emp.department || '', position: emp.position || '', hire_date: emp.hire_date || '',
        employment_type: emp.employment_type || '', worker_classification: emp.worker_classification || '',
        salary: emp.salary ?? '', hourly_rate: emp.hourly_rate ?? '', pay_frequency: emp.pay_frequency || '',
        bank_name: emp.bank_name || '', bank_account: emp.bank_account || '', routing_number: emp.routing_number || '',
        tax_retention_percent: emp.tax_retention_percent ?? 0,
        accrual_law: emp.accrual_law || 'post_2017',
        account_type: emp.account_type || 'checking', emergency_contact_name: emp.emergency_contact_name || '',
        emergency_contact_phone: emp.emergency_contact_phone || '', emergency_contact_relationship: emp.emergency_contact_relationship || '',
        notes: emp.notes || ''
      });
    }
  }, [selectedEmployee]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`/hr/employees/${selectedEmployee.user_id}/profile`, profile);
      toast.success('Perfil guardado exitosamente');
      loadEmployees();
      loadLeaveBalance(selectedEmployee.user_id);
    } catch (error) {
      toast.error('Error al guardar perfil');
    }
    setSaving(false);
  };

  const loadLeaveBalance = async (userId) => {
    try {
      const res = await axios.get(`/hr/employees/${userId}/leave-balance`);
      setLeaveBalance(res.data);
    } catch (error) {
      setLeaveBalance(null);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    if (!window.confirm(`¿Estás seguro de eliminar a "${selectedEmployee.name}"? Se eliminarán todos sus datos, documentos y registros de ponche.`)) return;
    try {
      await axios.delete(`/hr/employees/${selectedEmployee.user_id}`);
      toast.success('Empleado eliminado');
      setSelectedEmployee(null);
      setDocuments([]);
      setProfile(emptyProfile);
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.document_type) {
      toast.error('Selecciona tipo y archivo');
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    try {
      await axios.post(`/hr/employees/${selectedEmployee.user_id}/documents?document_type=${uploadForm.document_type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento subido');
      setUploadDialogOpen(false);
      setUploadForm({ document_type: '', file: null });
      loadDocuments(selectedEmployee.user_id);
    } catch (error) {
      toast.error('Error al subir');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try {
      await axios.delete(`/hr/employees/${selectedEmployee.user_id}/documents/${docId}`);
      toast.success('Eliminado');
      loadDocuments(selectedEmployee.user_id);
    } catch (error) {
      toast.error('Error');
    }
  };

  const getDocumentUrl = (doc) => `${BACKEND_URL}/api/hr/employees/${doc.employee_id}/documents/${doc.doc_id}/download`;

  const isPreviewable = (filename) => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };

  const getFileExtension = (filename) => filename ? filename.toLowerCase().split('.').pop() : '';

  const loadDocumentPreview = async (doc) => {
    setPreviewDoc(doc);
    setLoadingPreview(true);
    setPreviewBlobUrl(null);
    try {
      const response = await axios.get(`/hr/employees/${doc.employee_id}/documents/${doc.doc_id}/preview`, { responseType: 'blob' });
      setPreviewBlobUrl(URL.createObjectURL(response.data));
    } catch (error) {
      toast.error('Error al cargar vista previa');
      setPreviewDoc(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewDoc(null);
  };

  const filteredEmployees = employees.filter(e =>
    (e.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <>
      <div className="flex h-[calc(100vh-200px)] border rounded-xl overflow-hidden" data-testid="hr-panel">
        {/* Sidebar - Employee List */}
        <div className="w-72 border-r bg-slate-50/50 flex flex-col">
          <div className="p-4 border-b bg-white">
            <h2 className="text-lg font-semibold text-slate-800">Empleados</h2>
            <div className="relative mt-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <Input placeholder="Buscar..." className="pl-9 h-9 bg-slate-50" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="hr-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {filteredEmployees.map(emp => (
              <div key={emp.user_id} onClick={() => setSelectedEmployee(emp)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${selectedEmployee?.user_id === emp.user_id ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-white hover:shadow-sm'}`}
                data-testid={`employee-item-${emp.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${selectedEmployee?.user_id === emp.user_id ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                    {getInitials(emp.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">{emp.name || 'Sin nombre'}</p>
                    <p className="text-xs text-slate-500 truncate">{emp.profile?.position || emp.role || ''}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-white">
          {!selectedEmployee ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Users className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400">Selecciona un empleado</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="border-b bg-gradient-to-r from-slate-50 to-white p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-600">
                    {getInitials(selectedEmployee.name)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800">{selectedEmployee.name || 'Sin nombre'}</h2>
                    <p className="text-slate-500">{profile.position || 'Sin cargo'} {profile.department ? `• ${profile.department}` : ''}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">{selectedEmployee.role}</Badge>
                      <Badge className={profile.worker_classification === 'contractor' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}>
                        {profile.worker_classification === 'contractor' ? 'Contratista' : 'Empleado'}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto text-red-600 border-red-200 hover:bg-red-50"
                    onClick={handleDeleteEmployee} data-testid="hr-delete-employee">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </div>
              </div>

              <div className="border-b px-6">
                <div className="flex gap-6">
                  {[{ id: 'info', label: 'Información' }, { id: 'payment', label: 'Pago' }, { id: 'stubs', label: 'Talonarios' }, { id: 'docs', label: 'Documentos' }].map(tab => (
                    <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                      className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {activeSection === 'info' && (
                  <div className="space-y-6 max-w-3xl">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Personal</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Teléfono" icon={Phone}><Input value={profile.phone} onChange={(e) => updateProfile('phone', e.target.value)} /></Field>
                        <Field label="Fecha Nacimiento" icon={Calendar}><Input type="date" value={profile.date_of_birth} onChange={(e) => updateProfile('date_of_birth', e.target.value)} /></Field>
                        <Field label="ID / Cédula"><Input value={profile.id_number} onChange={(e) => updateProfile('id_number', e.target.value)} /></Field>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Dirección</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Dirección"><Input value={profile.address} onChange={(e) => updateProfile('address', e.target.value)} /></Field>
                        <Field label="Ciudad"><Input value={profile.city} onChange={(e) => updateProfile('city', e.target.value)} /></Field>
                        <Field label="Código Postal"><Input value={profile.zipcode} onChange={(e) => updateProfile('zipcode', e.target.value)} /></Field>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Building className="w-4 h-4" /> Empleo</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Cargo"><Input value={profile.position} onChange={(e) => updateProfile('position', e.target.value)} /></Field>
                        <Field label="Departamento"><Input value={profile.department} onChange={(e) => updateProfile('department', e.target.value)} /></Field>
                        <Field label="Fecha Ingreso" icon={Calendar}><Input type="date" value={profile.hire_date} onChange={(e) => updateProfile('hire_date', e.target.value)} /></Field>
                        <Field label="Clasificación">
                          <Select value={profile.worker_classification} onValueChange={(v) => updateProfile('worker_classification', v)}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                            <SelectContent><SelectItem value="employee">Empleado</SelectItem><SelectItem value="contractor">Contratista</SelectItem></SelectContent>
                          </Select>
                        </Field>
                        <Field label="Tipo">
                          <Select value={profile.employment_type} onValueChange={(v) => updateProfile('employment_type', v)}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                            <SelectContent><SelectItem value="full_time">Tiempo Completo</SelectItem><SelectItem value="part_time">Medio Tiempo</SelectItem></SelectContent>
                          </Select>
                        </Field>
                        <Field label="Ley de Licencias Aplicable">
                          <Select value={profile.accrual_law || 'post_2017'} onValueChange={(v) => updateProfile('accrual_law', v)}>
                            <SelectTrigger data-testid="accrual-law-select"><SelectValue placeholder="Seleccionar ley" /></SelectTrigger>
                            <SelectContent>
                              {accrualLawOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                      {/* Accrual Law Info Card */}
                      {(() => {
                        const law = profile.accrual_law || 'post_2017';
                        const hireDate = profile.hire_date ? new Date(profile.hire_date) : null;
                        const now = new Date();
                        const yearsOfService = hireDate ? ((now - hireDate) / (365.25 * 24 * 60 * 60 * 1000)) : 0;
                        const minHours = law === 'pre_2017_115' ? 115 : 130;

                        // Accumulated months since hire
                        const monthsOfService = hireDate ? Math.max(0, Math.floor((now - hireDate) / (30.4375 * 24 * 60 * 60 * 1000))) : 0;

                        // Compute accumulated vacation days bracket-by-bracket (post_2017 only)
                        let accumulatedVac = 0;
                        let vacRate = 0;
                        const sickRate = 1.0;

                        if (law === 'pre_2017_115' || law === 'pre_2017_130') {
                          vacRate = 1.25;
                          accumulatedVac = monthsOfService * 1.25;
                        } else if (law === 'small_employer') {
                          vacRate = 0.50;
                          accumulatedVac = monthsOfService * 0.50;
                        } else { // post_2017 — bracket-based
                          const brackets = [
                            { years: 1, rate: 0.50 },
                            { years: 5, rate: 0.75 },
                            { years: 15, rate: 1.00 },
                            { years: Infinity, rate: 1.25 },
                          ];
                          let remaining = monthsOfService;
                          let startYear = 0;
                          for (const br of brackets) {
                            const bracketMonths = Math.min(remaining, Math.max(0, (br.years - startYear) * 12));
                            accumulatedVac += bracketMonths * br.rate;
                            remaining -= bracketMonths;
                            startYear = br.years;
                            if (remaining <= 0) break;
                          }
                          if (yearsOfService <= 1) vacRate = 0.50;
                          else if (yearsOfService <= 5) vacRate = 0.75;
                          else if (yearsOfService <= 15) vacRate = 1.00;
                          else vacRate = 1.25;
                        }
                        const accumulatedSick = monthsOfService * sickRate;

                        return (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2" data-testid="accrual-info">
                            <p className="text-xs font-semibold text-blue-800 mb-2">
                              Acumulación de Licencias ({accrualLawOptions.find(o => o.value === law)?.label || law})
                            </p>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                              <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                                <p className="text-lg font-bold text-blue-700">{minHours}h</p>
                                <p className="text-[10px] text-slate-500">Mínimo horas/mes</p>
                              </div>
                              <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                                <p className="text-lg font-bold text-green-600">{vacRate.toFixed(2)}</p>
                                <p className="text-[10px] text-slate-500">Días vac./mes</p>
                              </div>
                              <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                                <p className="text-lg font-bold text-orange-600">{sickRate.toFixed(2)}</p>
                                <p className="text-[10px] text-slate-500">Días enf./mes</p>
                              </div>
                              <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                                <p className="text-lg font-bold text-slate-700">{yearsOfService.toFixed(1)}</p>
                                <p className="text-[10px] text-slate-500">Años de servicio</p>
                              </div>
                            </div>

                            {/* ACCUMULATED BALANCE — NEW */}
                            {hireDate && (
                              <div className="mt-3 pt-3 border-t border-blue-200">
                                <p className="text-xs font-semibold text-slate-700 mb-2">
                                  Balance acumulado desde {hireDate.toLocaleDateString('es-PR', { day: 'numeric', month: 'short', year: 'numeric' })} ({monthsOfService} meses de servicio)
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 text-center text-white">
                                    <p className="text-2xl font-bold" data-testid="accrued-vacation-days">{(leaveBalance?.vacation?.accrued ?? accumulatedVac).toFixed(1)}</p>
                                    <p className="text-[11px] opacity-90">días de vacaciones acumulados</p>
                                  </div>
                                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-3 text-center text-white">
                                    <p className="text-2xl font-bold" data-testid="accrued-sick-days">{(leaveBalance?.sick?.accrued ?? accumulatedSick).toFixed(1)}</p>
                                    <p className="text-[11px] opacity-90">días de enfermedad acumulados</p>
                                  </div>
                                </div>

                                {/* Balance real con descuento de licencias aprobadas */}
                                {leaveBalance && (
                                  <div className="mt-3 grid grid-cols-2 gap-3" data-testid="leave-balance-detail">
                                    <div className="bg-white rounded-lg p-3 border border-green-200">
                                      <p className="text-[11px] font-semibold text-green-700 mb-1">Vacaciones</p>
                                      <div className="flex justify-between text-[11px] text-slate-600"><span>Acumulados</span><span className="font-mono">{leaveBalance.vacation.accrued.toFixed(1)}</span></div>
                                      <div className="flex justify-between text-[11px] text-slate-600"><span>Usados</span><span className="font-mono text-red-600">−{leaveBalance.vacation.used.toFixed(1)}</span></div>
                                      <div className="flex justify-between text-xs font-bold text-green-700 mt-1 pt-1 border-t"><span>Disponibles</span><span className="font-mono" data-testid="vacation-available">{leaveBalance.vacation.available.toFixed(1)}</span></div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-orange-200">
                                      <p className="text-[11px] font-semibold text-orange-700 mb-1">Enfermedad</p>
                                      <div className="flex justify-between text-[11px] text-slate-600"><span>Acumulados</span><span className="font-mono">{leaveBalance.sick.accrued.toFixed(1)}</span></div>
                                      <div className="flex justify-between text-[11px] text-slate-600"><span>Usados</span><span className="font-mono text-red-600">−{leaveBalance.sick.used.toFixed(1)}</span></div>
                                      <div className="flex justify-between text-xs font-bold text-orange-700 mt-1 pt-1 border-t"><span>Disponibles</span><span className="font-mono" data-testid="sick-available">{leaveBalance.sick.available.toFixed(1)}</span></div>
                                    </div>
                                  </div>
                                )}
                                <p className="text-[10px] text-slate-500 mt-2">
                                  {leaveBalance
                                    ? '✅ Los días "usados" se descuentan automáticamente de las solicitudes de licencia APROBADAS.'
                                    : '⚠️ Guarda el perfil (ley + fecha de contratación) para calcular el balance con descuento de licencias.'}
                                </p>
                              </div>
                            )}

                            {law === 'post_2017' && (
                              <div className="mt-3 pt-3 border-t border-blue-100 text-[10px] text-blue-700 space-y-0.5">
                                <p className="font-semibold mb-1">Tabla post-2017 (Ley 4-2017):</p>
                                <p className={yearsOfService <= 1 ? 'font-bold' : ''}>0-1 año: 0.50 día/mes {yearsOfService <= 1 ? '(actual)' : ''}</p>
                                <p className={yearsOfService > 1 && yearsOfService <= 5 ? 'font-bold' : ''}>{'>'} 1-5 años: 0.75 día/mes {yearsOfService > 1 && yearsOfService <= 5 ? '(actual)' : ''}</p>
                                <p className={yearsOfService > 5 && yearsOfService <= 15 ? 'font-bold' : ''}>{'>'} 5-15 años: 1.00 día/mes {yearsOfService > 5 && yearsOfService <= 15 ? '(actual)' : ''}</p>
                                <p className={yearsOfService > 15 ? 'font-bold' : ''}>{'>'} 15 años: 1.25 días/mes {yearsOfService > 15 ? '(actual)' : ''}</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">Contacto de Emergencia</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Nombre"><Input value={profile.emergency_contact_name} onChange={(e) => updateProfile('emergency_contact_name', e.target.value)} /></Field>
                        <Field label="Teléfono"><Input value={profile.emergency_contact_phone} onChange={(e) => updateProfile('emergency_contact_phone', e.target.value)} /></Field>
                        <Field label="Relación"><Input value={profile.emergency_contact_relationship} onChange={(e) => updateProfile('emergency_contact_relationship', e.target.value)} /></Field>
                      </div>
                    </div>
                    <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="hr-save-profile">
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                )}

                {activeSection === 'payment' && (
                  <div className="space-y-6 max-w-3xl">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Compensación</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Salario Fijo"><Input type="number" value={profile.salary} onChange={(e) => updateProfile('salary', e.target.value)} placeholder="0.00" /></Field>
                        <Field label="Tarifa por Hora"><Input type="number" value={profile.hourly_rate} onChange={(e) => updateProfile('hourly_rate', e.target.value)} placeholder="0.00" /></Field>
                        <Field label="Frecuencia de Pago">
                          <Select value={profile.pay_frequency} onValueChange={(v) => updateProfile('pay_frequency', v)}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="weekly">Semanal</SelectItem><SelectItem value="biweekly">Bisemanal</SelectItem>
                              <SelectItem value="semimonthly">Quincenal</SelectItem><SelectItem value="monthly">Mensual</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Retención Hacienda (%)">
                          <Input type="number" step="0.01" min="0" max="100" value={profile.tax_retention_percent} onChange={(e) => updateProfile('tax_retention_percent', parseFloat(e.target.value) || 0)} placeholder="0" data-testid="hr-tax-retention-input" />
                        </Field>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Información Bancaria</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Banco"><Input value={profile.bank_name} onChange={(e) => updateProfile('bank_name', e.target.value)} /></Field>
                        <Field label="Número de Cuenta"><Input value={profile.bank_account} onChange={(e) => updateProfile('bank_account', e.target.value)} /></Field>
                        <Field label="Routing Number"><Input value={profile.routing_number} onChange={(e) => updateProfile('routing_number', e.target.value)} /></Field>
                        <Field label="Tipo de Cuenta">
                          <Select value={profile.account_type} onValueChange={(v) => updateProfile('account_type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="checking">Checking</SelectItem><SelectItem value="savings">Savings</SelectItem></SelectContent>
                          </Select>
                        </Field>
                      </div>
                    </div>
                    <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                )}

                {activeSection === 'stubs' && (
                  <div className="space-y-4 max-w-4xl" data-testid="hr-stubs-section">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Receipt className="w-4 h-4" /> Talonarios de Pago</h3>
                      <Button variant="outline" size="sm" onClick={() => loadEmployeeStubs(selectedEmployee.user_id)} data-testid="hr-refresh-stubs">
                        <Download className="w-4 h-4 mr-1" /> Actualizar
                      </Button>
                    </div>
                    {loadingStubs ? (
                      <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
                    ) : employeeStubs.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed rounded-lg" data-testid="hr-stubs-empty">
                        <Receipt className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-400">No hay talonarios para este empleado</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="hr-stubs-table">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3">Periodo</th>
                              <th className="text-right p-3">Horas</th>
                              <th className="text-right p-3">Bruto</th>
                              <th className="text-right p-3">Hacienda</th>
                              <th className="text-right p-3">Seg. Social</th>
                              <th className="text-right p-3">Medicare</th>
                              <th className="text-right p-3">Deducciones</th>
                              <th className="text-right p-3">Neto</th>
                              <th className="text-left p-3">Método</th>
                              <th className="text-left p-3">Fecha</th>
                            </tr>
                          </thead>
                          <tbody>
                            {employeeStubs.map((stub, idx) => (
                              <tr key={stub.id || idx} className="border-b hover:bg-slate-50" data-testid={`hr-stub-row-${idx}`}>
                                <td className="p-3 font-medium text-xs">{stub.period_start} - {stub.period_end}</td>
                                <td className="p-3 text-right font-mono text-xs">{(stub.hours_worked || 0).toFixed(1)}h</td>
                                <td className="p-3 text-right font-mono text-xs">${(stub.gross_pay || 0).toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-xs text-orange-600">${(stub.hacienda || 0).toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-xs text-blue-600">${(stub.ss || 0).toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-xs text-purple-600">${(stub.medicare || 0).toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-xs text-red-600">-${(stub.total_deductions || 0).toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-xs font-bold text-green-600">${(stub.net_pay || 0).toFixed(2)}</td>
                                <td className="p-3 text-xs capitalize">{stub.payment_method || 'N/A'}</td>
                                <td className="p-3 text-xs text-slate-500">{stub.created_at ? new Date(stub.created_at).toLocaleDateString('es-ES') : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-100 font-bold text-xs">
                            <tr>
                              <td className="p-3">TOTALES ({employeeStubs.length})</td>
                              <td className="p-3 text-right font-mono">{employeeStubs.reduce((a, s) => a + (s.hours_worked || 0), 0).toFixed(1)}h</td>
                              <td className="p-3 text-right font-mono">${employeeStubs.reduce((a, s) => a + (s.gross_pay || 0), 0).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-orange-600">${employeeStubs.reduce((a, s) => a + (s.hacienda || 0), 0).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-blue-600">${employeeStubs.reduce((a, s) => a + (s.ss || 0), 0).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-purple-600">${employeeStubs.reduce((a, s) => a + (s.medicare || 0), 0).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-red-600">-${employeeStubs.reduce((a, s) => a + (s.total_deductions || 0), 0).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-green-600">${employeeStubs.reduce((a, s) => a + (s.net_pay || 0), 0).toFixed(2)}</td>
                              <td colSpan="2" className="p-3"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeSection === 'docs' && (
                  <div className="space-y-4 max-w-3xl">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-slate-800">Documentos</h3>
                      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700"><Upload className="w-4 h-4 mr-2" /> Subir</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Subir Documento</DialogTitle></DialogHeader>
                          <form onSubmit={handleUpload} className="space-y-4">
                            <div>
                              <Label>Tipo</Label>
                              <Select value={uploadForm.document_type} onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}>
                                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                <SelectContent>{documentTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Archivo</Label>
                              <Input type="file" onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})} />
                            </div>
                            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Subir</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {documents.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed rounded-lg">
                        <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-400">No hay documentos</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documents.map(doc => (
                          <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium">{doc.original_filename || doc.filename}</p>
                                <p className="text-xs text-slate-500">{documentTypes.find(t => t.value === doc.document_type)?.label} • {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {isPreviewable(doc.original_filename) && (
                                <Button variant="ghost" size="sm" onClick={() => loadDocumentPreview(doc)}><Eye className="w-4 h-4 text-blue-500" /></Button>
                              )}
                              <Button variant="ghost" size="sm" asChild><a href={getDocumentUrl(doc)} target="_blank" rel="noreferrer"><Download className="w-4 h-4" /></a></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.doc_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && handleClosePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              {previewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] flex items-center justify-center bg-slate-100">
            {loadingPreview ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-slate-500">Cargando...</p>
              </div>
            ) : previewBlobUrl && previewDoc ? (
              getFileExtension(previewDoc.original_filename) === 'pdf' ? (
                <iframe src={previewBlobUrl} className="w-full h-[70vh] border-0 rounded" title="Vista previa" />
              ) : (
                <img src={previewBlobUrl} alt={previewDoc.original_filename} className="max-w-full max-h-[70vh] object-contain rounded shadow-lg" />
              )
            ) : (
              <p className="text-slate-500">No se puede cargar la vista previa</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HumanResourcesTab;
